// !!! DO NOT TOUCH: '@webtrader/core/init' import must be at the top
import '@webtrader/core/init';
import 'normalize.css';

import { datadogRum } from '@datadog/browser-rum';
import React, { useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Router } from 'react-router-dom';
import styled from 'styled-components/native';

import { ELoginSource } from '@webtrader/core/api/user/types';
import { isPROD } from '@webtrader/core/configs';
import { ELoginAs } from '@webtrader/core/constants/account';
import { EChartSource } from '@webtrader/core/constants/chart';
import { useCompanySettings } from '@webtrader/core/hooks/company';
import { isRTL, setLanguageChangeRestartCallback } from '@webtrader/core/i18n';
import { authService } from '@webtrader/core/services/auth';
import { chartService } from '@webtrader/core/services/chart';
import { companyService } from '@webtrader/core/services/company';
import { themeService } from '@webtrader/core/services/theme/v2';
import { EThemeScheme } from '@webtrader/core/themes';
import { mapNativeSchemeToColors } from '@webtrader/core/themes/native';
import { mapSchemeToColors } from '@webtrader/core/themes/utils';
import { validateCompanyAccess } from '@webtrader/core/validators/company';

import Screens from './screens';
import { useInitMobileAlert } from './services/alert';
import { resetLayoutOnLanguageChange } from './utils/language';
import { history, useRouteQueryParamsSearch } from './utils/navigation.web';

const MobileWebRoot = () => {
  const { company } = useCompanySettings();
  const { token } = useRouteQueryParamsSearch();

  useInitMobileAlert();

  const { hasExperienceAccess, hasMT5DemoLoginConfig } = useMemo(() => {
    return validateCompanyAccess(company);
  }, [company]);

  useEffect(() => {
    const init = () => {
      authService.init(ELoginSource.MOBILE_WEB);
    };

    const initServices = async () => {
      themeService.initMobile(mapNativeSchemeToColors());

      // init language change restart callback
      setLanguageChangeRestartCallback(() => {
        resetLayoutOnLanguageChange();
      });

      await companyService.init();

      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function () {
        if (themeService.context.theme.scheme === EThemeScheme.SYSTEM) {
          const nextDeviceColors = mapSchemeToColors();

          themeService.setSystemColors(nextDeviceColors);
          themeService.checkAndSwitchSystemTheme();
        }
      });

      if (token) {
        if (hasExperienceAccess) {
          const loginAs = hasMT5DemoLoginConfig ? ELoginAs.MT5_DEMO : ELoginAs.MT4_DEMO;
          authService.setLoginAs(loginAs);
        }
        authService.initWithToken(token, init);
      } else {
        init();
      }

      chartService.setSource(EChartSource.MOBILEWEB);
    };

    initServices();

    if (isPROD()) {
      // This Datadog function will only run on PROD, the init values will be populated only in PROD builds
      datadogRum.init({
        applicationId: '9f22a944-860e-45d9-8b49-4c01496cae1e',
        clientToken: 'pubb9f7ab2e5cbf36e2df1eeaf5931dc926',
        site: 'us5.datadoghq.com',
        service: 'webtrader-mobileweb',
        env: 'prod',
        // version: '1.0.0',
        sessionSampleRate: 25,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input'
      });
    }

    return () => {};
  }, [token, hasExperienceAccess, hasMT5DemoLoginConfig]);

  return (
    <StyledGestureHandlerRootView>
      <StyledWrapper dir={isRTL() ? 'rtl' : 'ltr'}>
        <Router history={history}>
          <Screens />
        </Router>
      </StyledWrapper>
    </StyledGestureHandlerRootView>
  );
};

export default MobileWebRoot;

const StyledGestureHandlerRootView = styled(GestureHandlerRootView)`
  flex: 1;
`;

const StyledWrapper = styled(View)`
  flex: 1;
`;
